import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { restaurantsService } from '../../services/restaurantsService';
import { RestaurantResponse, ApplicationStatus } from '../../types/restaurants';
import './Page.css';

const Restaurants: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [restaurants, setRestaurants] = useState<RestaurantResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const pageSize = 20;

  // Read from URL params or use defaults
  const searchQuery = searchParams.get('search') || '';
  const statusParam = searchParams.get('status');
  const currentFilter = statusParam === ''
    ? null
    : (statusParam || ApplicationStatus.ACCEPTED) as ApplicationStatus;
  const currentPage = parseInt(searchParams.get('page') || '0', 10);

  // Helper functions to update URL params
  const updateSearchQuery = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    newParams.set('page', '0');
    setSearchParams(newParams);
  };

  const updateFilter = (value: ApplicationStatus | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === null) {
      newParams.set('status', ''); // Set to empty string for ALL
    } else {
      newParams.set('status', value);
    }
    newParams.set('page', '0');
    setSearchParams(newParams);
  };

  const updatePage = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page.toString());
    setSearchParams(newParams);
  };

  useEffect(() => {
    fetchRestaurants();
  }, [searchQuery, currentFilter, currentPage]);

  const fetchRestaurants = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await restaurantsService.getRestaurants({
        searchQuery,
        page: currentPage,
        pageSize,
        applicationStatus: currentFilter
      });

      if (response.success && response.data) {
        setRestaurants(response.data);
      } else {
        setError(response.error || 'Failed to fetch restaurants');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const search = formData.get('search') as string;
    updateSearchQuery(search);
  };

  const handleFilterChange = (filter: ApplicationStatus | null) => {
    updateFilter(filter);
  };

  const handleRowClick = (restaurantId: number) => {
    navigate(`/restaurants/${restaurantId}`);
  };

  const formatPrice = (price: number): string => {
    return `${price.toLocaleString()} soum`;
  };

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const stars = [];

    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(
          <span key={i} style={{ color: '#FFD700', fontSize: '1rem' }}>★</span>
        );
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(
          <span key={i} style={{ color: '#FFD700', fontSize: '1rem' }}>☆</span>
        );
      } else {
        stars.push(
          <span key={i} style={{ color: '#ddd', fontSize: '1rem' }}>★</span>
        );
      }
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <div style={{ display: 'flex', gap: '1px' }}>{stars}</div>
        <span style={{ fontSize: '0.9rem', color: '#666', marginLeft: '4px' }}>
          {rating.toFixed(1)}
        </span>
      </div>
    );
  };

  const getStatusColor = (status: ApplicationStatus): string => {
    switch (status) {
      case ApplicationStatus.PENDING:
        return '#ffc107';
      case ApplicationStatus.ACCEPTED:
        return '#28a745';
      case ApplicationStatus.BLOCKED:
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  const getStatusDisplay = (status: ApplicationStatus): string => {
    switch (status) {
      case ApplicationStatus.PENDING:
        return t('restaurants.statusFilter.pending');
      case ApplicationStatus.ACCEPTED:
        return t('restaurants.statusFilter.accepted');
      case ApplicationStatus.BLOCKED:
        return t('restaurants.statusFilter.blocked');
      default:
        return status;
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>{t('restaurants.title')}</h1>
        <p>{t('restaurants.subtitle')}</p>
      </div>

      <div className="page-content">
        {/* Search Section */}
        <div className="search-section">
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-controls">
              <input
                type="text"
                name="search"
                placeholder={t('restaurants.searchRestaurants')}
                defaultValue={searchQuery}
                className="search-input"
              />
              <button type="submit" className="search-button">
                {t('common.search')}
              </button>
            </div>
          </form>
        </div>

        {/* Filters Section */}
        <div className="filters-section">
          <h3>{t('restaurants.applicationStatus')}</h3>
          <div className="filter-tags">
            <button
              className={`filter-tag ${currentFilter === null ? 'active' : ''}`}
              onClick={() => handleFilterChange(null)}
            >
              {t('restaurants.statusFilter.all')}
            </button>
            <button
              className={`filter-tag ${currentFilter === ApplicationStatus.PENDING ? 'active' : ''}`}
              onClick={() => handleFilterChange(ApplicationStatus.PENDING)}
            >
              {t('restaurants.statusFilter.pending')}
            </button>
            <button
              className={`filter-tag ${currentFilter === ApplicationStatus.ACCEPTED ? 'active' : ''}`}
              onClick={() => handleFilterChange(ApplicationStatus.ACCEPTED)}
            >
              {t('restaurants.statusFilter.accepted')}
            </button>
            <button
              className={`filter-tag ${currentFilter === ApplicationStatus.BLOCKED ? 'active' : ''}`}
              onClick={() => handleFilterChange(ApplicationStatus.BLOCKED)}
            >
              {t('restaurants.statusFilter.blocked')}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && <div className="error-message">{error}</div>}

        {/* Restaurants Table */}
        <div className="table-container">
          {loading ? (
            <div className="loading-container">{t('restaurants.loadingRestaurants')}</div>
          ) : restaurants.length === 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('restaurants.restaurantId')}</th>
                  <th>{t('common.name')}</th>
                  <th>{t('common.phone')}</th>
                  <th>Hours</th>
                  <th>{t('restaurants.rating')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('restaurants.balance')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={7} className="no-data">
                    {t('restaurants.noRestaurants')}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('restaurants.restaurantId')}</th>
                  <th>{t('common.name')}</th>
                  <th>{t('common.phone')}</th>
                  <th>Hours</th>
                  <th>{t('restaurants.rating')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('restaurants.balance')}</th>
                </tr>
              </thead>
              <tbody>
                {restaurants.map((restaurant) => (
                  <tr
                    key={restaurant.id}
                    onClick={() => handleRowClick(restaurant.id)}
                    className="clickable-row"
                  >
                    <td>{restaurant.id}</td>
                    <td>
                      <div>{restaurant.name}</div>
                      <div className="sub-text">
                        {restaurant.isOpen ? '🟢 Open' : '🔴 Closed'}
                      </div>
                    </td>
                    <td>{restaurant.phone}</td>
                    <td>
                      {restaurant.workStartTime} - {restaurant.workEndTime}
                    </td>
                    <td>{renderStars(restaurant.rating)}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(restaurant.applicationStatus) }}
                      >
                        {getStatusDisplay(restaurant.applicationStatus)}
                      </span>
                    </td>
                    <td>{formatPrice(restaurant.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!loading && restaurants.length > 0 && (
          <div className="pagination">
            <button
              className="pagination-button"
              onClick={() => updatePage(currentPage - 1)}
              disabled={currentPage === 0}
            >
              {t('common.previous')}
            </button>
            <span className="pagination-info">{t('orders.page')} {currentPage + 1}</span>
            <button
              className="pagination-button"
              onClick={() => updatePage(currentPage + 1)}
              disabled={restaurants.length < pageSize}
            >
              {t('common.next')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Restaurants;
